import {Component, OnInit, ViewChild} from '@angular/core';
import {LitigeSavedSupplier, LitigesService} from '../../core/services/entities/litiges.service';
import {HELP_FOLDERS, MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {OverlayPanel} from "primeng/overlaypanel";
import {Subscription} from "rxjs";
import {LitigeDTO} from "../../core/dtos/litige-dto";
import {Auth2Service} from "../../core/services/security/auth2.service";
import {InternationalizationService} from "../../core/services/i8n/i8n.service";
import {GraphQLService} from "../../core/services/technique/graphql.service";
import {UtilsService} from "../../core/utils/utils.service";
import {ToastService} from "../../core/services/technique/toast.service";
import {ResponseWrapper} from "../../core/suppliers/wrappers/response-wrapper";
import {IsDeletableObject} from "../../core/models/is-deletable-object";
import {confirm} from "devextreme/ui/dialog";
import {saveAs} from "file-saver";

@Component({
  selector: 'yo-litiges',
  templateUrl: './litiges.component.html',
  styleUrls: ['./litiges.component.scss']
})
export class LitigesComponent implements OnInit {
  @ViewChild('opImportXls') opImportXls: OverlayPanel;

  subLog: Subscription;
  subLitigeSaved: Subscription;
  subLitiges: Subscription;

  logged: boolean = false;

  litiges: LitigeDTO[] = [];

  selectedRows: number[] = [];
  errors: any[] = [];

  displayImportPopupErrors: boolean = false;
  displayDeletePopupErrors: boolean = false;

  pathHelpFile: string = HELP_FOLDERS.REFERENTIEL + '/litiges/litiges';

  constructor(private auth2Svc: Auth2Service,
              private i8nSvc: InternationalizationService,
              private graphQlSvc: GraphQLService,
              public utilsSvc: UtilsService,
              public litigesSvc: LitigesService,
              public toastSvc: ToastService) {
  }

  ngOnInit() {
    this.initLoggedSubscription();
    this.initLitigesSubscription();
    this.initLitigeSavedSubscription();
  }

  ngOnDestroy(): void {
    this.utilsSvc.unsubscribe(this.subLog);
    this.utilsSvc.unsubscribe(this.subLitigeSaved);
    this.utilsSvc.unsubscribe(this.subLitiges);
  }

  initLoggedSubscription = (): void => {
    this.subLog = this.auth2Svc.isLoggedIn$
      .subscribe(res => this.logged = res);
  }

  initLitigesSubscription = (): void => {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    this.subLitiges = this.graphQlSvc.sendQuery(`
      {
        allLitiges(filters: {
          siteIds: [${idsSites}]
        }) {
            id,
            code,
            libelle,
            actif,
            site {
                id,
                libelle,
            },
        }
      }
    `)
      .subscribe((response: any) => {
        console.log(response.allLitiges);
        this.litiges = response.allLitiges;
      });
  }

  initLitigeSavedSubscription = (): void => {
    this.subLitigeSaved = this.litigesSvc.litigeSaved$
      .subscribe((response: LitigeSavedSupplier) => {
        if (response.isUpdate) {
          const idx: number = this.litiges.findIndex(litige => litige.id === response.litige.id);
          this.litiges[idx] = response.litige;
        } else {
          this.litiges.push(response.litige);
        }
      });
  };

  openDialogEdition = (litige: LitigeDTO): void => {
    this.litigesSvc.announceOpenDialogEdition(litige);
  };

  calculateSiteValue = (litige: LitigeDTO): string => {
    return litige?.site?.libelle
  };

  exportXslx = () => {
    this.litigesSvc.exportLitigeByFilters(this.selectedRows).subscribe(response => {
      const filename = `litige-export-${new Date().getTime()}.xlsx`;
      const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
      this.selectedRows = [];
      saveAs(blob, filename);
    });
  }

  importXslx = async ($event: any) => {
    this.errors = [];
    if ($event.target.files && $event.target.files.length) {
      const file: File = $event.target.files[0];
      this.opImportXls.hide();
      this.litigesSvc.importLitigeFromXlsx(file)
        .subscribe(response => {
          const res: any = response.one;
          if (res.allElementsImported)
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le fichier a été importé avec succès`);
          else {
            for (const item of Object.entries(res.reports)) {
              let values: any[] = Array.of(item[1]);
              const labelsError: any[] = values.map(val => val.map(v => this.i8nSvc.getLabelFromCode(v.code, v.args.split(','))).join(', '));
              this.errors.push({infosLine: item[0], labelsError});
            }
          }
          for (const item of res.elementsImported) {
            let index = this.litiges.findIndex(litige => litige.id == item.id);
            if (index >= 0)
              this.litiges[index] = item;
            else
              this.litiges.push(item)
          }

          if (this.errors != null && this.errors.length > 0)
            this.displayImportErrors();
        });
    }
  }

  displayImportErrors = (): void => {
    this.displayImportPopupErrors = true;
  }

  displayDeleteErrors = (): void => {
    this.displayDeletePopupErrors = true;
  }

  closeErrors = (): void => {
    this.errors = [];
    this.displayImportPopupErrors = false;
    this.displayDeletePopupErrors = false;
  }

  deleteValues = async (): Promise<void> => {
    this.errors = [];
    let idsToDeleteInTable = Object.assign([], this.selectedRows);

    let isDeletionValidated: boolean = await confirm(this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? "CONFIRM_DELETION_PLURAL" : "CONFIRM_DELETION_SINGULAR", null), this.i8nSvc.getLabelFromCode("Suppression", null));

    if (!isDeletionValidated)
      return new Promise(null);

    this.litigesSvc.deleteLitigeByFilters(this.selectedRows).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res.messagesErrorList && res.messagesErrorList.length > 0) {
          for (const error of res.messagesErrorList) {
            const infosLine: string = this.i8nSvc.getLabelFromCode(error.code, error.args.split(','));
            this.errors.push({infosLine});
            const elementToKeep = this.litiges.find(litige => litige.code === error.args);
            idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
          }
          this.displayDeleteErrors();
        } else {
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? `LITIGE_DELETION_COMPLETED_PLURAL` : `LITIGE_DELETION_COMPLETED_SINGULAR`, null));
        }
        this.litiges = this.litiges.filter(litige => !idsToDeleteInTable.find(id => id === litige.id));
      }
    });
  }

  canDelete = (): boolean => this.selectedRows.length > 0;


}
